// Historia Clínica imprimible (ficha 360° → Historia Clínica), estilo reliv:
// modal "Seleccionar logos y firmas" (fecha, firma Manual/Digital, médico, y
// logo: empresa | logo personal del médico | Sin Logo) que compila TODO el
// expediente del paciente (med_historia_completa) en un documento imprimible
// con pie de página de la clínica en cada hoja.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, fieldCls, medRpc } from './shared';
import type { Diagnostico, EvolucionItem, Historia, MedicoRow, SignoVital } from './shared';
import { calcIMC } from './FormularioClinico';
import { fechaLocal } from '@/lib/utils/fecha';
import { edadAl } from './EsquemaVacunacion';
import { PATOLOGIAS, resumenAntecedente } from './Antecedentes';
import type { AntecedenteRow, AntecedentesData } from './Antecedentes';
import { LOCALE } from '@/lib/utils/regional';

const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const s = (d: Record<string, unknown> | null | undefined, k: string): string =>
  (d && typeof d[k] === 'string' ? (d[k] as string) : '');
const fila = (l: string, v: string) => (v?.trim() ? `<tr><td class="l">${esc(l)}</td><td>${esc(v)}</td></tr>` : '');
// Misión del paciente (= centro de costo) resaltada, en la misma línea del nombre.
const misionHtml = (cod?: string | null, nom?: string | null) => (nom?.trim()
  ? `<span style="display:inline-block;border:2px solid #000;border-radius:4px;padding:1px 6px;margin-left:8px;font-weight:800;text-transform:uppercase;font-size:12px;white-space:nowrap;vertical-align:middle;"><span style="font-size:9px;font-weight:600;opacity:.7;">MISIÓN </span>${esc(cod ? cod + ' · ' : '')}${esc(nom)}</span>`
  : '');
const campo = (l: string, v?: string | null) => (v?.trim() ? `<h4>${esc(l)}</h4><p>${esc(v)}</p>` : '');
const fmtF = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(v.includes('T') ? v : `${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface HCompleta {
  empresa: { nombre: string; ruc: string | null; direccion: string | null; telefono: string | null; email: string | null; logo: string | null };
  detalle: { paciente: Record<string, unknown>; partner: Record<string, unknown>; documentos: unknown[]; centro_costo: { codigo: string | null; nombre: string } | null };
  antecedentes: AntecedentesData;
  historia: Historia;
  recetas: { receta: Record<string, unknown>; items: Record<string, unknown>[] }[];
  examenes_ficha: { id: string; categoria: string; numero: number | null; fecha: string; datos: Record<string, unknown>; medico: string | null }[];
  citas: { proxima: unknown; citas: { fecha: string; hora_inicio: string | null; estado: string; medico: string; especialidad: string | null }[] };
}

const dxHtml = (dx?: Diagnostico[] | null) => (dx?.length
  ? `<table><thead><tr><th>Código</th><th>CIE-10</th><th class="c">Presuntivo</th><th class="c">Definitivo</th></tr></thead><tbody>${dx.map((x) => `<tr><td>${esc(x.cie10_codigo ?? '—')}</td><td>${esc(x.descripcion)}</td><td class="c">${x.tipo === 'presuntivo' ? '●' : ''}</td><td class="c">${x.tipo === 'definitivo' ? '●' : ''}</td></tr>`).join('')}</tbody></table>` : '');

const signosHtml = (sig?: SignoVital) => (sig
  ? `<p class="sig"><b>FC:</b> ${esc(sig.frecuencia_cardiaca ?? '—')} · <b>FR:</b> ${esc(sig.frecuencia_resp ?? '—')} · <b>SO2:</b> ${esc(sig.spo2 ?? '—')} · <b>TA:</b> ${sig.presion_sistolica != null ? `${sig.presion_sistolica}/${sig.presion_diastolica ?? '—'}` : '—'} · <b>T°:</b> ${esc(sig.temperatura ?? '—')} · <b>Peso:</b> ${esc(sig.peso ?? '—')} kg · <b>Talla:</b> ${esc(sig.talla ?? '—')} · <b>IMC:</b> ${esc(calcIMC(sig.peso, sig.talla) ?? '—')}</p>` : '');

// Bloque de antecedente general (familiar/personal) para el documento
function antecedenteHtml(titulo: string, row: AntecedenteRow | null): string {
  if (!row) return '';
  const d = row.datos ?? {};
  const pat = (d.patologias ?? {}) as Record<string, string>;
  const hab = (d.habitos ?? {}) as Record<string, string>;
  const gin = (d.gineco ?? {}) as Record<string, string>;
  if (!resumenAntecedente(row)) return '';
  const patHtml = PATOLOGIAS.filter(([k]) => k in pat)
    .map(([k, l]) => fila(l, pat[k]?.trim() || 'Sí')).join('');
  const habHtml = Object.entries(hab).map(([k, v]) => fila(`Hábito — ${k.replace(/_/g, ' ')}`, v?.trim() || 'Sí')).join('');
  const ginHtml = Object.entries(gin).filter(([, v]) => v?.trim()).map(([k, v]) => fila(`Gineco — ${k.replace(/_/g, ' ')}`, v)).join('');
  const textos = Object.entries(d)
    .filter(([k, v]) => !['patologias', 'habitos', 'gineco'].includes(k) && typeof v === 'string' && (v as string).trim())
    .map(([k, v]) => fila(k.replace(/_/g, ' '), v as string)).join('');
  const cuerpo = row.sin_patologia ? '<p>SIN PATOLOGÍA APARENTE</p>'
    : `<table><tbody>${patHtml}${habHtml}${ginHtml}${textos}</tbody></table>`;
  return `<h3>${esc(titulo)}</h3>${cuerpo}`;
}

export function ModalHistoriaClinica({ pacienteId, nombre, onClose }: { pacienteId: string; nombre: string; onClose: () => void }) {
  const { data: hc, isLoading } = useQuery<HCompleta>({
    queryKey: ['med-ficha', pacienteId, 'hc-completa'],
    queryFn: () => medRpc<HCompleta>('med_historia_completa', { _paciente_id: pacienteId }),
  });
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  // El médico responsable del documento es el usuario logueado (no se elige). Admin/recepción mantiene el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';

  const [fecha, setFecha] = useState(fechaLocal());
  const [firma, setFirma] = useState<'manual' | 'digital'>('manual');
  const [medicoId, setMedicoId] = useState('');
  const [logo, setLogo] = useState<'empresa' | 'medico' | 'ninguno'>('empresa');
  const medico = medicos.find((m) => m.id === medicoId);
  // Si la sesión es de un médico, el responsable es el usuario logueado (no se elige).
  useEffect(() => {
    if (miMedicoId) setMedicoId(miMedicoId);
  }, [miMedicoId]);
  // Default de firma por médico (firma_modo de su ficha); el usuario puede overridearlo con el selector.
  useEffect(() => {
    if (medico) setFirma(medico.firma_modo === 'manual' ? 'manual' : 'digital');
  }, [medico?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const imprimir = () => {
    if (!hc) return;
    const pac = hc.detalle.paciente; const par = hc.detalle.partner; const cc = hc.detalle.centro_costo;
    const logoUrl = logo === 'empresa' ? hc.empresa.logo : logo === 'medico' ? (medico?.logo_url ?? null) : null;
    const fnac = typeof pac.fecha_nacimiento === 'string' ? (pac.fecha_nacimiento as string) : null;

    const filiacion = `
      <h3>Datos de filiación</h3>
      <table><tbody>
        ${par.razon_social ? `<tr><td class="l">Paciente</td><td><b>${esc(String(par.razon_social))}</b>${misionHtml(cc?.codigo, cc?.nombre)}</td></tr>` : ''}
        ${fila('Tipo de doc.', String(par.tipo_identificacion ?? ''))}
        ${fila('Número de documento', String(par.identificacion ?? ''))}
        ${fila('Número de paciente (HC)', String(pac.codigo ?? ''))}
        ${fila('Fecha de nacimiento', fmtF(fnac))}
        ${fila('Edad', edadAl(fnac, fecha))}
        ${fila('Sexo', pac.genero === 'M' ? 'Masculino' : pac.genero === 'F' ? 'Femenino' : String(pac.genero ?? ''))}
        ${fila('Email', String(par.email ?? ''))}
        ${fila('Celular', String(par.celular ?? ''))}
        ${fila('Teléfono', String(par.telefono ?? ''))}
        ${fila('Ciudad', String(par.ciudad ?? ''))}
        ${fila('Dirección', String(par.direccion ?? ''))}
      </tbody></table>
      <h3>Datos adicionales</h3>
      <table><tbody>
        ${fila('Nacionalidad', String(par.nacionalidad ?? ''))}
        ${fila('Ocupación', String(pac.ocupacion ?? ''))}
        ${fila('Estado civil', String(pac.estado_civil ?? ''))}
        ${fila('Tipo de sangre', String(pac.tipo_sangre ?? ''))}
        ${fila('Seguro médico', [pac.aseguradora, pac.numero_poliza].filter(Boolean).join(' · '))}
      </tbody></table>`;

    // Antecedentes
    const ant = hc.antecedentes;
    const antPed = (() => {
      const r = ant.pediatrico; if (!r || !resumenAntecedente(r)) return '';
      const secs = ['prenatales', 'perinatales', 'postnatales'] as const;
      const filas = secs.map((sec) => {
        const o = (r.datos[sec] ?? {}) as Record<string, unknown>;
        return Object.entries(o)
          .filter(([, v]) => (typeof v === 'string' ? (v as string).trim() : v === true))
          .map(([k, v]) => fila(`${sec} — ${k.replace(/_/g, ' ')}`, v === true ? 'Sí' : String(v))).join('');
      }).join('');
      return filas ? `<h3>Antecedentes Pediátricos</h3><table><tbody>${filas}</tbody></table>` : '';
    })();
    const antecedentesHtml = antecedenteHtml('Antecedentes Familiares', ant.familiar)
      + antecedenteHtml('Antecedentes Personales', ant.personal) + antPed;

    // Consultas (episodios con evoluciones)
    const todas = hc.historia.evoluciones ?? [];
    type EvPlus = EvolucionItem['evolucion'] & { episodio_id?: string | null; alta_at?: string | null; datos?: Record<string, unknown> | null };
    const raices = todas.filter((e) => !(e.evolucion as EvPlus).episodio_id);
    const consultasHtml = raices.map((e, i) => {
      const ev = e.evolucion as EvPlus;
      const hijas = todas.filter((h) => (h.evolucion as EvPlus).episodio_id === ev.id);
      const exCompl = typeof ev.datos?.examenes_complementarios === 'string' ? (ev.datos.examenes_complementarios as string) : '';
      const hijasHtml = hijas.map((h, j) => {
        const he = h.evolucion as EvPlus;
        const texto = typeof he.datos?.texto_evolucion === 'string' ? (he.datos.texto_evolucion as string) : '';
        return `<div class="evo"><b>EVOLUCIÓN #${j + 1} — ${esc(fmtF(he.fecha))} · ${esc(h.medico)}</b>
          ${signosHtml(h.signos?.[0])}${campo('Evolución', texto)}${campo('Subjetivo', he.subjetivo)}${campo('Objetivo', he.objetivo)}
          ${campo('Análisis', he.analisis)}${campo('Plan', he.plan)}${dxHtml(h.diagnosticos)}${campo('Observaciones', he.observaciones)}</div>`;
      }).join('');
      return `<div class="consulta"><h3>Consulta ${raices.length - i} — ${esc(fmtF(ev.fecha))} · ${esc(e.medico)}${e.especialidad ? ` · ${esc(e.especialidad)}` : ''}${ev.alta_at ? ' · DADO DE ALTA' : ''}</h3>
        ${signosHtml(e.signos?.[0])}${campo('Motivo de consulta', ev.motivo)}${campo('Subjetivo - Anamnesis', ev.subjetivo)}
        ${campo('Objetivo - Examen físico', ev.objetivo)}${campo('Exámenes complementarios', exCompl)}
        ${campo('Análisis - Diagnóstico', ev.analisis)}${dxHtml(e.diagnosticos)}
        ${campo('Plan - Tratamiento', ev.plan)}${campo('Observaciones', ev.observaciones)}${hijasHtml}</div>`;
    }).join('');

    // Recetas
    const recetasHtml = hc.recetas.map((r) => {
      const rec = r.receta; const datos = (rec.datos ?? {}) as Record<string, unknown>;
      const items = r.items.map((it) => `<tr><td>${esc(it.medicamento)}</td><td>${esc(it.comercial ?? '')}</td><td>${esc(it.concentracion ?? '')}</td><td>${esc(it.via ?? '')}</td><td>${esc(it.forma ?? '')}</td><td class="c">${esc(it.cantidad ?? '')}</td><td>${esc(it.indicaciones ?? '')}</td></tr>`).join('');
      return `<div class="consulta"><h3>Receta ${esc(rec.codigo)} — ${esc(fmtF(String(rec.fecha)))}</h3>
        ${campo('Diagnóstico', typeof rec.diagnostico === 'string' ? (rec.diagnostico as string) : '')}
        ${items ? `<table><thead><tr><th>Genérico</th><th>Comercial</th><th>Concentración</th><th>Vía</th><th>Presentación</th><th class="c">Cantidad</th><th>Indicación</th></tr></thead><tbody>${items}</tbody></table>` : ''}
        ${Array.isArray(datos.signos_alarma) && (datos.signos_alarma as string[]).length ? campo('Signos de alarma', (datos.signos_alarma as string[]).join(' · ')) : ''}
        ${campo('Recomendaciones no farmacológicas', s(datos, 'recomendaciones'))}</div>`;
    }).join('');

    // Vacunas
    const vacunas = hc.historia.vacunas ?? [];
    const vacunasHtml = vacunas.length
      ? `<h3>Vacunas</h3><table><thead><tr><th>Vacuna</th><th>Dosis</th><th>Fecha</th><th>Próxima</th></tr></thead><tbody>${vacunas.map((v) => `<tr><td>${esc(v.vacuna)}</td><td>${esc(v.dosis ?? '')}</td><td>${esc(fmtF(v.fecha))}</td><td>${esc(fmtF(v.proxima_fecha))}</td></tr>`).join('')}</tbody></table>` : '';

    // Registros clínicos de la ficha (sin cobros/presupuestos/formularios)
    const TITULOS: Record<string, string> = {
      laboratorio: 'Análisis de Laboratorio', imagen: 'Estudios de Imagen', especialidad: 'Exámenes por Especialidad',
      patologia: 'Patología', pedido: 'Pedidos', procedimiento: 'Procedimientos', informe: 'Informes',
      consentimiento_informado: 'Consentimientos Informados', orden_ingreso: 'Órdenes de Ingreso',
      interconsulta: 'Interconsultas', chequeo_preop: 'Chequeos Preoperatorios', protocolo_operatorio: 'Protocolos Operatorios',
    };
    const resumenReg = (r: HCompleta['examenes_ficha'][number]): string => {
      const d = r.datos;
      switch (r.categoria) {
        case 'laboratorio': return [s(d, 'resultados') && `Resultados: ${s(d, 'resultados')}`, s(d, 'analisis') && `Análisis: ${s(d, 'analisis')}`].filter(Boolean).join(' — ');
        case 'imagen': return [s(d, 'tipo_examen'), s(d, 'descripcion')].filter(Boolean).join(' — ');
        case 'especialidad': return `Revisión de ${s(d, 'especialidad')}`;
        case 'patologia': return [`Estudio: ${s(d, 'estudio')}`, s(d, 'conclusiones') && `Conclusiones: ${s(d, 'conclusiones')}`].filter(Boolean).join(' — ');
        case 'pedido': return [s(d, 'tipo_pedido'), Array.isArray(d.items) ? (d.items as { nombre: string }[]).map((x) => x.nombre).join(', ') : ''].filter(Boolean).join(' — ');
        case 'procedimiento': return [s(d, 'procedimiento'), s(d, 'descripcion')].filter(Boolean).join(' — ');
        case 'informe': return s(d, 'informe').slice(0, 300);
        case 'consentimiento_informado': return s(d, 'procedimiento');
        case 'orden_ingreso': return [s(d, 'hospital'), s(d, 'servicio')].filter(Boolean).join(' — ');
        case 'interconsulta': return [s(d, 'servicio_interconsulta'), s(d, 'motivo')].filter(Boolean).join(' — ');
        case 'chequeo_preop': return s(d, 'diagnostico') || s(d, 'motivo');
        case 'protocolo_operatorio': return [s(d, 'operacion_realizada') || s(d, 'operacion_proyectada'), s(d, 'dx_postoperatorio')].filter(Boolean).join(' — ');
        default: return '';
      }
    };
    const porCat = new Map<string, HCompleta['examenes_ficha']>();
    (hc.examenes_ficha ?? []).forEach((r) => {
      if (!TITULOS[r.categoria]) return;
      porCat.set(r.categoria, [...(porCat.get(r.categoria) ?? []), r]);
    });
    const registrosHtml = [...porCat.entries()].map(([cat, regs]) =>
      `<h3>${esc(TITULOS[cat])}</h3><table><tbody>${regs.map((r) => fila(`N° ${r.numero ?? ''} · ${fmtF(r.fecha)}`, resumenReg(r) || '(registrado)')).join('')}</tbody></table>`).join('');

    // Citas
    const citas = hc.citas?.citas ?? [];
    const citasHtml = citas.length
      ? `<h3>Citas</h3><table><thead><tr><th>Fecha</th><th>Hora</th><th>Médico</th><th>Estado</th></tr></thead><tbody>${citas.map((c) => `<tr><td>${esc(fmtF(c.fecha))}</td><td>${esc(c.hora_inicio?.slice(0, 5) ?? '')}</td><td>${esc(c.medico)}</td><td>${esc(c.estado)}</td></tr>`).join('')}</tbody></table>` : '';

    const firmaHtml = medico
      ? `<div class="firma">${firma === 'digital' && medico.firma_url ? `<img src="${medico.firma_url}" alt="firma" style="height:60px">` : '<div style="height:60px"></div>'}
         <div class="linea"></div><b>Dr(a). ${esc(medico.razon_social)}</b><br>${esc(medico.especialidad ?? '')}${medico.numero_licencia ? `<br>Reg. ${esc(medico.numero_licencia)}` : ''}</div>`
      : '';

    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Historia Clínica — ${esc(nombre)}</title>
      <style>*{font-family:Arial,Helvetica,sans-serif}body{padding-bottom:46px}
      h1{font-size:17px;margin:0;text-align:center;letter-spacing:1px}h2{font-size:14px;margin:2px 0 0;text-align:center}
      .fecha{font-size:11px;text-align:center;color:#555;margin:2px 0 12px}
      h3{font-size:12px;margin:14px 0 3px;background:#eef4f8;padding:3px 6px;border-left:3px solid #4a90a4;text-transform:uppercase}
      h4{font-size:11px;margin:7px 0 1px;text-transform:uppercase;color:#333}p{font-size:11px;margin:0;white-space:pre-wrap}
      table{border-collapse:collapse;width:100%;font-size:10.5px;margin-top:2px}td,th{border:1px solid #bbb;padding:3px 5px;vertical-align:top;white-space:pre-wrap}
      td.l{width:210px;font-weight:bold;background:#f7f7f7}th{background:#eee}.c{text-align:center}
      .sig{font-size:10.5px;margin:4px 0;color:#333}.consulta{page-break-inside:avoid;margin-bottom:8px}
      .evo{margin:6px 0 0 14px;padding-left:8px;border-left:2px solid #ccc;font-size:11px}
      .cab{display:flex;align-items:center;gap:14px;margin-bottom:8px}.cab img{height:52px;object-fit:contain}
      .firma{margin-top:40px;width:280px;text-align:center;font-size:11px}.firma .linea{border-top:1px solid #333;margin-bottom:3px}
      .pie{position:fixed;bottom:0;left:0;right:0;border-top:1px solid #999;font-size:9.5px;color:#555;padding:3px 0;text-align:center;background:#fff}
      @media print{@page{margin:14mm 12mm 18mm}}</style></head><body>
      <div class="cab">
        ${logoUrl ? `<img src="${logoUrl}" alt="logo">` : ''}
        <div style="flex:1">
          <h1>HISTORIA CLÍNICA</h1>
          <h2>${esc(nombre)}</h2>
          ${medico ? `<p style="text-align:center">Dr(a). ${esc(medico.razon_social)}${medico.especialidad ? ` — Especialidad: ${esc(medico.especialidad)}` : ''}</p>` : ''}
          <p class="fecha">Fecha: ${esc(fmtF(fecha))}</p>
        </div>
      </div>
      ${filiacion}
      ${antecedentesHtml}
      ${consultasHtml ? `<h3 style="page-break-before:always">Consultas Médicas</h3>${consultasHtml}` : ''}
      ${recetasHtml ? `<h3>Recetas</h3>${recetasHtml}` : ''}
      ${vacunasHtml}${registrosHtml}${citasHtml}
      ${firmaHtml}
      <div class="pie">Dirección: ${esc(hc.empresa.direccion ?? '')} · Teléfono: ${esc(hc.empresa.telefono ?? '')} · Correo electrónico: ${esc(hc.empresa.email ?? '')}</div>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
    onClose();
  };

  return (
    <Modal title="Seleccionar logos y firmas" onClose={onClose}>
      {isLoading || !hc ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
      ) : (
        <div className="space-y-3">
          <Campo label="Fecha de impresión"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Seleccione firma">
            <select className={fieldCls} value={firma} onChange={(e) => setFirma(e.target.value as 'manual' | 'digital')}>
              <option value="manual">Firma Manual</option>
              <option value="digital">Firma Digital</option>
            </select>
          </Campo>
          <Campo label="Médico responsable *">
            {miMedicoId ? (
              <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
            ) : (
              <select className={fieldCls} value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
                <option value="">— Selecciona —</option>
                {medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
              </select>
            )}
          </Campo>
          {firma === 'digital' && medico && !medico.firma_url && (
            <p className="text-xs text-amber-600">Este médico no tiene firma digital guardada — se imprimirá con espacio para firma manual.</p>
          )}
          <div>
            <span className="text-xs font-semibold uppercase text-muted-foreground">Seleccione logo</span>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
              <label className={`cursor-pointer rounded-md border p-2 ${logo === 'empresa' ? 'border-primary ring-1 ring-primary' : ''}`}>
                <input type="radio" className="accent-primary" name="hc-logo" checked={logo === 'empresa'} onChange={() => setLogo('empresa')} />
                <div className="mt-1 flex h-14 items-center justify-center">
                  {hc.empresa.logo ? <img src={hc.empresa.logo} alt="Logo empresa" className="max-h-14 object-contain" /> : <span className="text-muted-foreground">Logo empresa</span>}
                </div>
                <span>Empresa</span>
              </label>
              <label className={`cursor-pointer rounded-md border p-2 ${logo === 'medico' ? 'border-primary ring-1 ring-primary' : ''} ${!medico?.logo_url ? 'opacity-50' : ''}`}>
                <input type="radio" className="accent-primary" name="hc-logo" disabled={!medico?.logo_url} checked={logo === 'medico'} onChange={() => setLogo('medico')} />
                <div className="mt-1 flex h-14 items-center justify-center">
                  {medico?.logo_url ? <img src={medico.logo_url} alt="Logo médico" className="max-h-14 object-contain" /> : <span className="text-muted-foreground">Sin logo del médico</span>}
                </div>
                <span>Del médico</span>
              </label>
              <label className={`cursor-pointer rounded-md border p-2 ${logo === 'ninguno' ? 'border-primary ring-1 ring-primary' : ''}`}>
                <input type="radio" className="accent-primary" name="hc-logo" checked={logo === 'ninguno'} onChange={() => setLogo('ninguno')} />
                <div className="mt-1 flex h-14 items-center justify-center text-muted-foreground">—</div>
                <span>Sin Logo</span>
              </label>
            </div>
          </div>
          <div className="flex justify-center gap-2 border-t pt-3">
            <Button size="sm" disabled={!medicoId} onClick={imprimir}
                    title="En el diálogo elige «Guardar como PDF» para obtener el archivo">
              <Printer className="w-4 h-4 mr-1" /> Imprimir
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
